import React from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Home, LogOut, ChevronDown, Building2 } from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import { getNavLinksForVariant } from './DashboardNavLinks';

/**
 * Mobile navigation content for dashboard flyouts
 * 
 * Props:
 * - variant: 'admin' | 'business' | 'venue'
 * - user: user object
 * - onClose: function to close drawer
 * - businesses: array of businesses (for business variant)
 * - currentBusiness: current selected business
 * - onBusinessSelect: function to handle business selection
 * - venueData: venue info (for venue variant)
 * - badges: object with badge counts { activeDeals: 3, coasterInventory: 1250, etc }
 */
export default function DashboardMobileNav({
  variant = 'business',
  user,
  onClose,
  businesses = [],
  currentBusiness = null,
  onBusinessSelect,
  venueData = null,
  badges = {},
}) {
  const { logout } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  
  const navSections = getNavLinksForVariant(variant);
  
  // Variant-specific colors
  const colors = {
    admin: {
      primary: '#ef4444',
      badge: 'bg-red-500',
      badgeText: 'text-white',
      activeBg: 'bg-red-500/20',
      activeText: 'text-red-400',
      // Static, so Tailwind's purge keeps them (never interpolate class names).
      actionHover: 'hover:bg-red-500/20 hover:text-red-400',
      gradient: 'from-red-500 to-red-600',
      label: 'ADMIN',
    },
    business: {
      primary: '#8b5cf6',
      badge: 'bg-purple-500',
      badgeText: 'text-white',
      activeBg: 'bg-purple-500/20',
      activeText: 'text-purple-400',
      // Static, so Tailwind's purge keeps them (never interpolate class names).
      actionHover: 'hover:bg-purple-500/20 hover:text-purple-400',
      gradient: 'from-purple-500 to-purple-600',
      label: 'BUSINESS',
    },
    venue: {
      primary: '#14b8a6',
      badge: 'bg-teal-500',
      badgeText: 'text-white',
      activeBg: 'bg-teal-500/20',
      activeText: 'text-teal-400',
      // Static, so Tailwind's purge keeps them (never interpolate class names).
      actionHover: 'hover:bg-teal-500/20 hover:text-teal-400',
      gradient: 'from-teal-500 to-teal-600',
      label: 'VENUE',
    },
  };
  
  const c = colors[variant];
  
  // Check if path is active
  const isActive = (path) => location.pathname === path || location.pathname.startsWith(path + '/');
  
  // Get user initials
  const getInitials = () => {
    if (!user) return '?';
    const name = user.display_name || user.name || user.first_name || user.email || '';
    return name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2) || '?';
  };
  
  // Get display name
  const getDisplayName = () => {
    if (!user) return 'User';
    return user.display_name || user.name || user.first_name || user.email?.split('@')[0] || 'User';
  };
  
  // Handle logout
  const handleLogout = async () => {
    await logout();
    onClose();
    navigate('/');
  };
  
  // Handle navigation
  const handleNavigate = (path) => {
    navigate(path);
    onClose();
  };

  return (
    <div className="space-y-6">
      {/* User Profile Card */}
      <div className="bg-white/5 rounded-2xl p-4 border border-white/10">
        <div className="flex items-center gap-3">
          <div 
            className={`w-12 h-12 rounded-full flex items-center justify-center text-white font-bold text-lg bg-gradient-to-br ${c.gradient}`}
          >
            {getInitials()}
          </div>
          <div className="flex-1 min-w-0">
            <div className="font-semibold text-white truncate">{getDisplayName()}</div>
            <div className="text-xs text-white/50 truncate">{user?.email}</div>
          </div>
          <div className={`px-2 py-1 rounded-full ${c.badge} ${c.badgeText}`}>
            <span className="text-[10px] font-bold">{c.label}</span>
          </div>
        </div>
      </div>
      
      {/* Business Switcher (Business variant only) */}
      {variant === 'business' && businesses.length > 0 && (
        <div>
          <p className="text-[11px] font-semibold text-white/40 uppercase tracking-wider mb-2 px-1">
            Your Business
          </p>
          <div className="bg-white/5 rounded-xl border border-white/10">
            {businesses.map((biz) => (
              <button
                key={biz.id}
                onClick={() => onBusinessSelect && onBusinessSelect(biz.id)}
                className={`w-full flex items-center gap-3 px-4 py-3 transition-colors ${
                  currentBusiness?.id === biz.id
                    ? `${c.activeBg} ${c.activeText}`
                    : 'text-white/70 hover:bg-white/5'
                }`}
              >
                <Building2 size={18} />
                <span className="flex-1 text-left text-sm font-medium truncate">
                  {biz.name || biz.business_name}
                </span>
                {currentBusiness?.id === biz.id && (
                  <div className="w-2 h-2 rounded-full" style={{ backgroundColor: c.primary }} />
                )}
              </button>
            ))}
          </div>
        </div>
      )}
      
      {/* Venue Info (Venue variant only) */}
      {variant === 'venue' && venueData && (
        <div>
          <p className="text-[11px] font-semibold text-white/40 uppercase tracking-wider mb-2 px-1">
            Your Venue
          </p>
          <div className="bg-white/5 rounded-xl border border-white/10 p-4">
            <div className="flex items-center gap-3">
              <div 
                className="w-10 h-10 rounded-lg flex items-center justify-center"
                style={{ backgroundColor: 'rgba(20, 184, 166, 0.2)' }}
              >
                <Building2 size={20} style={{ color: '#14b8a6' }} />
              </div>
              <div>
                <p className="text-white font-medium text-sm">{venueData.venue_name}</p>
                <p className="text-white/50 text-xs">{venueData.venue_type} • {venueData.city}</p>
              </div>
            </div>
          </div>
        </div>
      )}
      
      {/* Navigation Sections */}
      {navSections.map((section) => (
        <div key={section.section}>
          <p className="text-[11px] font-semibold text-white/40 uppercase tracking-wider mb-2 px-1">
            {section.section}
          </p>
          <div className="bg-white/5 rounded-xl border border-white/10 overflow-hidden">
            {section.links.map((link, idx) => {
              const Icon = link.icon;
              const active = isActive(link.path);
              const badgeValue = link.badgeKey ? badges[link.badgeKey] : null;
              
              return (
                <button
                  key={link.path}
                  onClick={() => handleNavigate(link.path)}
                  className={`w-full flex items-center gap-3 px-4 py-3 transition-colors ${
                    idx > 0 ? 'border-t border-white/5' : ''
                  } ${
                    active
                      ? `${c.activeBg} ${c.activeText}`
                      : link.isAction
                        ? `text-white/70 ${c.actionHover}`
                        : 'text-white/70 hover:bg-white/5 hover:text-white'
                  }`}
                >
                  <Icon size={18} />
                  <span className="flex-1 text-left text-sm font-medium">{link.label}</span>
                  {badgeValue !== null && badgeValue !== undefined && (
                    <span 
                      className="px-2 py-0.5 rounded-full text-xs font-bold"
                      style={{ backgroundColor: `${c.primary}30`, color: c.primary }}
                    >
                      {badgeValue}
                    </span>
                  )}
                  {active && (
                    <div className="w-2 h-2 rounded-full" style={{ backgroundColor: c.primary }} />
                  )}
                </button>
              );
            })}
          </div>
        </div>
      ))}
      
      {/* Divider */}
      <div className="border-t border-white/10" />
      
      {/* Footer Links */}
      <div className="space-y-2">
        <button
          onClick={() => handleNavigate('/')}
          className="w-full flex items-center gap-3 px-4 py-3 rounded-xl text-white/50 hover:bg-white/5 hover:text-white transition-colors"
        >
          <Home size={18} />
          <span className="text-sm font-medium">Go to Homepage</span>
        </button>
        
        <button
          onClick={handleLogout}
          className="w-full flex items-center gap-3 px-4 py-3 rounded-xl text-white/50 hover:bg-red-500/10 hover:text-red-400 transition-colors"
        >
          <LogOut size={18} />
          <span className="text-sm font-medium">Log Out</span>
        </button>
      </div>
    </div>
  );
}
